import { Box, Text } from '@chakra-ui/react'
import { useMemo } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  type Node,
  type Edge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { KrakenGraphResponse } from '../../../../types/kraken.types'
import {
  nodeTypes,
  edgeTypes,
  transformKrakenResponse,
  radialTreeLayout,
} from '../KrakenNetwork'

interface KrakenNetworkTreeProps {
  networkData: KrakenGraphResponse | null
  isLoading: boolean
  rootId?: string
  rootType?: string
}

const KrakenNetworkTreeInner = ({ networkData, isLoading, rootId }: KrakenNetworkTreeProps) => {
  const { nodes, edges } = useMemo<{ nodes: Node[]; edges: Edge[] }>(() => {
    if (!networkData?.nodes?.length) return { nodes: [], edges: [] }

    const { nodes: rfNodes, edges: rfEdges } = transformKrakenResponse(networkData)
    const laidOut = radialTreeLayout(rfNodes, rfEdges, rootId, {
      centerX: 0,
      centerY: 0,
    })

    return { nodes: laidOut, edges: rfEdges }
  }, [networkData, rootId])

  if (isLoading) {
    return (
      <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="12px" color="neutral.400">Building network...</Text>
      </Box>
    )
  }

  if (!networkData?.nodes?.length) {
    return (
      <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="12px" color="neutral.400">No network data</Text>
      </Box>
    )
  }

  return (
    <Box
      w="100%"
      h="100%"
      position="relative"
      sx={{
        // Force pointer-events on ReactFlow node wrappers — ReactFlow disables them
        // when nodesDraggable=false. Override at multiple levels for specificity.
        '.react-flow__node': { pointerEvents: 'all !important' },
        '.react-flow__node > *': { pointerEvents: 'all !important' },
        '.react-flow__node-toolbar': { pointerEvents: 'all !important' },
        '.react-flow__edgelabel-renderer > *': { pointerEvents: 'all' },
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        zoomOnScroll
        zoomOnPinch
        minZoom={0.05}
        maxZoom={1}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        onNodeClick={(_event, _node) => {}}
        defaultEdgeOptions={{
          type: 'smoothstep',
          markerEnd: { type: 'arrowclosed' as any, color: '#a78bfa' },
        }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          color="rgba(124, 58, 237, 0.15)"
        />
      </ReactFlow>
    </Box>
  )
}

const KrakenNetworkTree = (props: KrakenNetworkTreeProps) => (
  <ReactFlowProvider>
    <KrakenNetworkTreeInner {...props} />
  </ReactFlowProvider>
)

export default KrakenNetworkTree
